import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GalleryPage() {
  const [gallery, setGallery] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/gallery-categories`)
        ]);
        setGallery(galleryRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    document.title = 'Club JCI Ltd - Gallery';
  }, []);

  const filteredGallery = activeCategory === 'all' 
    ? gallery 
    : gallery.filter(item => item.category_id === activeCategory);

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'General';
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api/images/') 
      ? `${process.env.REACT_APP_BACKEND_URL}${url}/data` 
      : url;
  };

  // Lightbox navigation
  const openLightbox = (index) => setSelectedImageIndex(index);
  const closeLightbox = () => setSelectedImageIndex(null);
  
  const nextImage = () => {
    setSelectedImageIndex(prev => (prev + 1) % filteredGallery.length);
  };
  
  const prevImage = () => {
    setSelectedImageIndex(prev => (prev - 1 + filteredGallery.length) % filteredGallery.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, filteredGallery.length]);

  return (
    <div data-testid="gallery-page" className="pt-24">
      {/* Hero Section */}
      <section className="section-padding marble-overlay" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">Gallery</p>
            <h1 className="text-4xl sm:text-5xl text-[#F1F5F9] mb-6">
              Through the <span className="gold-text">Lenses</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              A visual journey through the elegance, events, and experiences at Club JCI.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding bg-[#0A192F] marble-overlay" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setActiveCategory('all')}
                data-testid="filter-all"
                className={`px-6 py-2 text-sm rounded-sm transition-all ${
                  activeCategory === 'all'
                    ? 'bg-[#D4AF37] text-[#051025]'
                    : 'bg-transparent border border-[#D4AF37]/30 text-[#D4AF37] hover:border-[#D4AF37]'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  data-testid={`filter-${category.name.toLowerCase()}`}
                  className={`px-6 py-2 text-sm rounded-sm transition-all ${
                    activeCategory === category.id
                      ? 'bg-[#D4AF37] text-[#051025]'
                      : 'bg-transparent border border-[#D4AF37]/30 text-[#D4AF37] hover:border-[#D4AF37]'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}

          {filteredGallery.length > 0 ? (
            <motion.div 
              layout
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              <AnimatePresence>
                {filteredGallery.map((item, index) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                    className="aspect-square rounded-sm overflow-hidden cursor-pointer group relative"
                    onClick={() => openLightbox(index)}
                    data-testid={`gallery-item-${item.id}`}
                  >
                    <img
                      src={getImageUrl(item.image_url)}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#051025]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-white text-sm font-medium">{item.title}</p>
                        <p className="text-[#D4AF37] text-xs">{getCategoryName(item.category_id)}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-6">📷</div>
              <h3 className="text-xl text-[#F1F5F9] mb-2">Gallery Coming Soon</h3>
              <p className="text-[#94A3B8]">
                Our collection of memorable moments will be shared here soon.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Lightbox with Navigation */}
      <AnimatePresence>
        {selectedImageIndex !== null && filteredGallery[selectedImageIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 transition-colors z-20"
              onClick={closeLightbox}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>

            {/* Previous button */}
            {filteredGallery.length > 1 && (
              <button
                className="lightbox-nav prev"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={24} />
              </button>
            )}

            {/* Image */}
            <motion.div
              key={selectedImageIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[85vh] px-4 md:px-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={getImageUrl(filteredGallery[selectedImageIndex].image_url)}
                alt={filteredGallery[selectedImageIndex].title}
                className="max-w-full max-h-[75vh] object-contain rounded-sm mx-auto"
              />
              <div className="text-center mt-4">
                <p className="text-white text-lg">{filteredGallery[selectedImageIndex].title}</p>
                <p className="text-[#D4AF37] text-sm">{getCategoryName(filteredGallery[selectedImageIndex].category_id)}</p>
                <p className="text-[#94A3B8] text-xs mt-2">
                  {selectedImageIndex + 1} / {filteredGallery.length} • Use arrow keys to navigate
                </p>
              </div>
            </motion.div>

            {/* Next button */}
            {filteredGallery.length > 1 && (
              <button
                className="lightbox-nav next"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                data-testid="lightbox-next"
              >
                <ChevronRight size={24} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
